/**
 * Кастомные React-хуки для админ-панели
 * Предоставляют удобный интерфейс для работы с API,
 * включая загрузку, ошибки, кэширование и toast-уведомления
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Route,
  Bus,
  Trip,
  TripWithDetails,
  getRoutes,
  getBuses,
  getTrips,
  getSchedule,
  createRoute,
  updateRoute,
  deleteRoute,
  createBus,
  updateBus,
  deleteBus,
  createTrip,
  updateTrip,
  deleteTrip,
} from './api';

// ============================================================
// Типы для toast-уведомлений
// ============================================================

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Глобальное хранилище toast-уведомлений (singleton)
let toastListeners: ((toasts: Toast[]) => void)[] = [];
let toasts: Toast[] = [];

function notifyToastListeners() {
  toastListeners.forEach((listener) => listener([...toasts]));
}

/** Показать toast-уведомление */
export function showToast(message: string, type: Toast['type'] = 'info') {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  toasts = [...toasts, { id, message, type }];
  notifyToastListeners();

  // Автоматически скрыть через 4 секунды
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    notifyToastListeners();
  }, 4000);
}

/** Хук для работы с toast-уведомлениями */
export function useToast() {
  const [state, setState] = useState<Toast[]>([]);

  useEffect(() => {
    toastListeners.push(setState);
    setState([...toasts]);
    return () => {
      toastListeners = toastListeners.filter((l) => l !== setState);
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    toasts = toasts.filter((t) => t.id !== id);
    notifyToastListeners();
  }, []);

  return { toasts: state, removeToast };
}

// ============================================================
// Универсальный хук для API-запросов
// ============================================================

interface UseApiOptions<T> {
  /** Функция для загрузки данных */
  fetcher: () => Promise<T>;
  /** Начальное значение данных */
  initialValue: T;
  /** Зависимости для перезагрузки */
  deps?: React.DependencyList;
}

interface UseApiResult<T> {
  /** Данные */
  data: T;
  /** Флаг загрузки */
  loading: boolean;
  /** Ошибка (если есть) */
  error: string | null;
  /** Функция для принудительной перезагрузки данных */
  refresh: () => void;
}

/**
 * Универсальный хук для работы с API
 * Автоматически загружает данные при монтировании и отслеживает состояние
 */
export function useApi<T>({ fetcher, initialValue, deps = [] }: UseApiOptions<T>): UseApiResult<T> {
  const [data, setData] = useState<T>(initialValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetcherRef.current();
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Ошибка загрузки данных';
          setError(message);
          showToast(message, 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, ...deps]);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return { data, loading, error, refresh };
}

// ============================================================
// Хук для работы с маршрутами
// ============================================================

interface UseRoutesReturn extends UseApiResult<Route[]> {
  /** Добавить новый маршрут */
  add: (data: Omit<Route, 'id'>) => Promise<Route | null>;
  /** Обновить маршрут */
  edit: (id: string, data: Partial<Route>) => Promise<Route | null>;
  /** Удалить маршрут */
  remove: (id: string) => Promise<boolean>;
  /** Найти маршрут по ID */
  getById: (id: string) => Route | undefined;
}

/** Хук для управления маршрутами */
export function useRoutes(): UseRoutesReturn {
  const api = useApi<Route[]>({
    fetcher: getRoutes,
    initialValue: [],
  });

  const getById = useCallback(
    (id: string) => api.data.find((r) => r.id === id),
    [api.data]
  );

  const add = useCallback(
    async (data: Omit<Route, 'id'>) => {
      try {
        const route = await createRoute(data);
        showToast(`Маршрут "${route.name}" создан`, 'success');
        api.refresh();
        return route;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка создания маршрута';
        showToast(message, 'error');
        return null;
      }
    },
    [api]
  );

  const edit = useCallback(
    async (id: string, data: Partial<Route>) => {
      try {
        const route = await updateRoute(id, data);
        showToast(`Маршрут "${route.name}" обновлён`, 'success');
        api.refresh();
        return route;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка обновления маршрута';
        showToast(message, 'error');
        return null;
      }
    },
    [api]
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await deleteRoute(id);
        showToast('Маршрут удалён', 'success');
        api.refresh();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка удаления маршрута';
        showToast(message, 'error');
        return false;
      }
    },
    [api]
  );

  return { ...api, getById, add, edit, remove };
}

// ============================================================
// Хук для работы с автобусами
// ============================================================

interface UseBusesReturn extends UseApiResult<Bus[]> {
  /** Добавить новый автобус */
  add: (data: Omit<Bus, 'id'>) => Promise<Bus | null>;
  /** Обновить автобус */
  edit: (id: string, data: Partial<Bus>) => Promise<Bus | null>;
  /** Удалить автобус */
  remove: (id: string) => Promise<boolean>;
  /** Найти автобус по ID */
  getById: (id: string) => Bus | undefined;
}

/** Хук для управления автобусами */
export function useBuses(): UseBusesReturn {
  const api = useApi<Bus[]>({
    fetcher: getBuses,
    initialValue: [],
  });

  const getById = useCallback(
    (id: string) => api.data.find((b) => b.id === id),
    [api.data]
  );

  const add = useCallback(
    async (data: Omit<Bus, 'id'>) => {
      try {
        const bus = await createBus(data);
        showToast(`Автобус "${bus.plateNumber}" добавлен`, 'success');
        api.refresh();
        return bus;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка добавления автобуса';
        showToast(message, 'error');
        return null;
      }
    },
    [api]
  );

  const edit = useCallback(
    async (id: string, data: Partial<Bus>) => {
      try {
        const bus = await updateBus(id, data);
        showToast(`Автобус "${bus.plateNumber}" обновлён`, 'success');
        api.refresh();
        return bus;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка обновления автобуса';
        showToast(message, 'error');
        return null;
      }
    },
    [api]
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await deleteBus(id);
        showToast('Автобус удалён', 'success');
        api.refresh();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка удаления автобуса';
        showToast(message, 'error');
        return false;
      }
    },
    [api]
  );

  return { ...api, getById, add, edit, remove };
}

// ============================================================
// Хук для работы с рейсами
// ============================================================

interface UseTripsReturn extends UseApiResult<Trip[]> {
  /** Добавить новый рейс */
  add: (data: Omit<Trip, 'id'>) => Promise<Trip | null>;
  /** Обновить рейс */
  edit: (id: string, data: Partial<Trip>) => Promise<Trip | null>;
  /** Удалить рейс */
  remove: (id: string) => Promise<boolean>;
  /** Найти рейс по ID */
  getById: (id: string) => Trip | undefined;
}

/** Хук для управления рейсами */
export function useTrips(): UseTripsReturn {
  const api = useApi<Trip[]>({
    fetcher: getTrips,
    initialValue: [],
  });

  const getById = useCallback(
    (id: string) => api.data.find((t) => t.id === id),
    [api.data]
  );

  const add = useCallback(
    async (data: Omit<Trip, 'id'>) => {
      try {
        const trip = await createTrip(data);
        showToast('Рейс создан', 'success');
        api.refresh();
        return trip;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка создания рейса';
        showToast(message, 'error');
        return null;
      }
    },
    [api]
  );

  const edit = useCallback(
    async (id: string, data: Partial<Trip>) => {
      try {
        const trip = await updateTrip(id, data);
        showToast('Рейс обновлён', 'success');
        api.refresh();
        return trip;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка обновления рейса';
        showToast(message, 'error');
        return null;
      }
    },
    [api]
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await deleteTrip(id);
        showToast('Рейс удалён', 'success');
        api.refresh();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Ошибка удаления рейса';
        showToast(message, 'error');
        return false;
      }
    },
    [api]
  );

  return { ...api, getById, add, edit, remove };
}

// ============================================================
// Хук для работы с расписанием на дату
// ============================================================

interface UseScheduleReturn extends UseApiResult<TripWithDetails[]> {
  /** Выбранная дата */
  date: string;
  /** Изменить дату */
  setDate: (date: string) => void;
}

/**
 * Хук для получения расписания на конкретную дату
 * По умолчанию — сегодняшняя дата
 */
export function useSchedule(): UseScheduleReturn {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);

  const api = useApi<TripWithDetails[]>({
    fetcher: useCallback(() => getSchedule(date), [date]),
    initialValue: [],
    deps: [date],
  });

  return { ...api, date, setDate };
}

// ============================================================
// Хук для подтверждения действий
// ============================================================

/**
 * Хук для показа диалога подтверждения
 * Возвращает функцию, которая возвращает Promise<boolean>
 */
export function useConfirm() {
  const confirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const confirmed = window.confirm(message);
      resolve(confirmed);
    });
  }, []);

  return confirm;
}

// ============================================================
// Хук для debounce
// ============================================================

/**
 * Хук для debounce значения
 * Полезен для поиска с задержкой
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
